import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Chip,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";

const statusStyles = {
  Active: { bgcolor: "#8CD47E", color: "#fff" },
  APPROVED: { bgcolor: "#8CD47E", color: "#fff" },
  Pending: { bgcolor: "#F8D66D", color: "#fff" },
  PENDING: { bgcolor: "#F8D66D", color: "#fff" },
  Rejected: { bgcolor: "#FF6961", color: "#fff" },
  REJECTED: { bgcolor: "#FF6961", color: "#fff" },
};


// Helper function to get frontend status display
const getFrontendStatus = (backendStatus) => {
  switch (backendStatus) {
    case 'APPROVED':
      return 'Active';
    case 'PENDING':
      return 'Pending';
    case 'REJECTED':
      return 'Rejected';
    default:
      return backendStatus || 'Pending';
  }
};

// Helper function to get backend status
const getBackendStatus = (frontendStatus) => {
  switch (frontendStatus) {
    case 'Active':
      return 'APPROVED';
    case 'Pending':
      return 'PENDING';
    case 'Rejected':
      return 'REJECTED';
    default:
      return frontendStatus?.toUpperCase() || 'PENDING';
  }
};

const EngineerEditModal = ({
  open,
  onClose,
  engineer: initialEngineer = null,
  onUpdate,
  showToast,
}) => {
  const { token } = useAuth();
  const [engineer, setEngineer] = useState(initialEngineer);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    status: "APPROVED",
    city: "",
    state: "",
    detailedAddress: "",
  });
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Input styles
// Input styles
const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 50,
    borderRadius: 1,
    "& fieldset": {
      borderColor: COLORS.PRIMARY, // Primary border when not focused
    },
    "&:hover fieldset": {
      borderColor: COLORS.PRIMARY, // Primary border on hover
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.PRIMARY, // Primary border when focused
    },
  },
  "& input": {
    color: COLORS.PRIMARY, // Primary text color
    fontWeight: 500,
  },
  "& .MuiInputLabel-root": {
    color: COLORS.SECONDARY,
  },
  "& .MuiSelect-select": {
    color: COLORS.PRIMARY, // Primary text color for select
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: COLORS.PRIMARY + " !important", // Force primary border
  },
  "& textarea": {
    color: COLORS.PRIMARY, // Primary text color for textarea
    fontWeight: 500,
  },
};

  // Initialize form data when engineer is provided
  useEffect(() => {
    if (initialEngineer) {
      setEngineer(initialEngineer);
      setFormData({
        name: initialEngineer.name || "",
        email: initialEngineer.email || "",
        phone: initialEngineer.phone || "",
        status: initialEngineer.status || "PENDING",
        city: initialEngineer.city || "",
        state: initialEngineer.state || "",
        detailedAddress: initialEngineer.detailedAddress || "",
      });
      setIsEditing(false);
    }
  }, [initialEngineer]);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!open) {
      setEngineer(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        status: "APPROVED",
        city: "",
        state: "",
        detailedAddress: "",
      });
      setIsEditing(false);
    }
  }, [open]);

  // Handle input changes
// Handle input changes
const handleInputChange = (field) => (event) => {
  let value = event.target.value;
  
  // Special handling for phone field
  if (field === 'phone') {
    // Remove anything that's not a digit
    value = value.replace(/[^0-9]/g, "");
    // Limit to 10 digits
    value = value.slice(0, 10);
  }
  
  setFormData(prev => ({
    ...prev,
    [field]: value
  }));
};

  // Handle status change
  const handleStatusChange = (event) => {
    setFormData(prev => ({
      ...prev,
      status: event.target.value
    }));
  };

  // Get project counts
  const getProjectCounts = (eng) => {
    if (!eng) return { completed: 0, assigned: 0, display: "0/0" };
    
    const completed = eng.projectsRecords?.projectCompleted || 0;
    const assigned = eng.projectsRecords?.projectAssigned || 0;
    
    return {
      completed,
      assigned,
      display: `${completed}/${assigned}`
    };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "2-digit",
}).replace(/\//g, '/')}

  // Validate form data
// Validate form data
// Validate form data
const validateForm = () => {
  // Name validation
  if (!formData.name.trim()) {
    showToast("error", "Validation Error", "Name is required");
    return false;
  }
  
  // Email validation
  if (!formData.email.trim()) {
    showToast("error", "Validation Error", "Email is required");
    return false;
  }
  
  // Email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email.trim())) {
    showToast("error", "Validation Error", "Please enter a valid email address");
    return false;
  }
  
  // Phone validation - exactly 10 digits
  if (!formData.phone.trim()) {
    showToast("error", "Validation Error", "Phone number is required");
    return false;
  }
  
  if (formData.phone.length !== 10) {
    showToast("error", "Validation Error", "Phone number must be exactly 10 digits");
    return false;
  }
  
  // Ensure only numbers
  if (!/^\d+$/.test(formData.phone)) {
    showToast("error", "Validation Error", "Phone number should contain only numbers");
    return false;
  }
  
  return true;
};
  // Handle update
  const handleUpdate = async () => {
    if (!validateForm()) return;
    
    try {
      setLoading(true);
      
      const updateData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        status: formData.status,
        city: formData.city.trim(),
        state: formData.state.trim(),
        detailedAddress: formData.detailedAddress.trim(),
      };

      const response = await axios.put(
        `${url}/api/users/update/${engineer._id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.status === 200) {
        showToast(
          "success",
          "Engineer Updated",
          `<strong>${engineer.name}'s</strong> details have been updated successfully`
        );

        if (onUpdate) {
          onUpdate(response.data.user); // Pass updated engineer data
        }

        setIsEditing(false);
        setEngineer(response.data.user);
        
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error updating engineer:", err);
      showToast(
        "error",
        "Update Failed",
        err.response?.data?.message || "Failed to update engineer details"
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (engineer) {
      setFormData({
        name: engineer.name || "",
        email: engineer.email || "",
        phone: engineer.phone || "",
        status: engineer.status || "PENDING",
        city: engineer.city || "",
        state: engineer.state || "",
        detailedAddress: engineer.detailedAddress || "",
      });
    }
    setIsEditing(false);
  };

  if (!open || !engineer) return null;

  const projectInfo = getProjectCounts(engineer);
  const frontendStatus = getFrontendStatus(formData.status);

  return (
    <>
      {/* Overlay */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#fff",
          borderRadius: 2,
          p: 3,
          zIndex: 1401,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
            Edit Engineer Details
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Engineer Info (Read-only view when not editing) */}
        {!isEditing ? (
          <>
            <Box sx={{ mb: 3 }}>
              <Box>
                {/* TOP ROW: Name + Joined Date */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  {/* Name + Status */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Icon name="profile" color={COLORS.PRIMARY} />
                    <Typography sx={TYPOGRAPHY.H3}>
                      {engineer.name}
                    </Typography>
                    <Chip
                      label={getFrontendStatus(engineer.status)}
                      size="small"
                      sx={{
                        ...(statusStyles[engineer.status] || statusStyles.Pending),
                        height: 22,
                        ...TYPOGRAPHY.BODY_SMALL,
                      }}
                    />
                  </Box>

                  {/* Joined Date */}
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                    Joined on: {formatDate(engineer.joinedAt || engineer.createdAt)}
                  </Typography>
                </Box>

                {/* Email */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, pl: "24px" }}>
                  <Icon name="at" size={16} color={COLORS.SECONDARY} />
                  <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}>
                    {engineer.email}
                  </Typography>
                </Box>

                {/* Phone */}
              {/* Phone */}
{/* Phone */}
<Box>
  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
    Phone Number*
  </Typography>
  <TextField
    fullWidth
    value={formData.phone}
    onChange={(e) => {
      // Remove anything that's not a digit
      const onlyNumbers = e.target.value.replace(/[^0-9]/g, "");
      // Limit to 10 digits
      const limitedNumbers = onlyNumbers.slice(0, 10);
      
      setFormData(prev => ({
        ...prev,
        phone: limitedNumbers,
      }));
    }}
    placeholder="Enter 10-digit phone number"
    type="tel"
    inputProps={{
      inputMode: "numeric",
      pattern: "[0-9]*",
      maxLength: 10,
    }}
    sx={{
      ...inputSx,
      // Add error styling
      "& .MuiOutlinedInput-root.Mui-error fieldset": {
        borderColor: COLORS.ERROR,
      },
    }}
    disabled={loading}
    error={formData.phone !== "" && formData.phone.length !== 10}
    helperText={
      formData.phone !== "" && formData.phone.length !== 10
        ? "Phone number must be exactly 10 digits"
        : ""
    }
  />
</Box>

                {/* Location + Projects (same row) */}
<Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 0.5,
    pl: "24px",
  }}
>
  {/* Location */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Icon name="location2" size={16} color={COLORS.SECONDARY} />
    <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}>
      {engineer.city && engineer.state
        ? `${engineer.city}, ${engineer.state}`
        : engineer.detailedAddress || "-"}
    </Typography>
  </Box>

  {/* Projects (right aligned) */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Icon name="project" size={16} color={COLORS.SECONDARY} />
    <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.SECONDARY }}>
      Projects: {projectInfo.display}
    </Typography>
  </Box>
</Box>

                {/* Projects */}
               
              </Box>
            </Box>

            <Divider sx={{ mb: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

            {/* Edit Button */}
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<Icon name="edit" />}
                onClick={() => setIsEditing(true)}
                sx={{
                  bgcolor: COLORS.PRIMARY,
                  "&:hover": { bgcolor: COLORS.PRIMARY },
                  textTransform: "none",
                }}
              >
                Edit Details
              </Button>
            </Box>
          </>
        ) : (
          /* Edit Form */
          <>
            <Box sx={{ mb: 3 }}>
              {/* Basic Information Section */}
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>
                Basic Information
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                {/* Name */}
                <Box>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                    Name*
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    placeholder="Enter engineer name"
                    sx={inputSx}
                    disabled={loading}
                  />
                </Box>

                {/* Email */}
                <Box>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                    Email Address*
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    placeholder="Enter email address"
                    type="email"
                    sx={inputSx}
                    disabled={loading}
                  />
                </Box>

                {/* Phone */}
                <Box>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                    Phone Number*
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    placeholder="Enter phone number"
                    sx={inputSx}
                    disabled={loading}
                  />
                </Box>

                {/* Status */}
                {/* <Box>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                    Status*
                  </Typography>
                  <FormControl fullWidth sx={inputSx}>
                    <Select
                      value={formData.status}
                      onChange={handleStatusChange}
                      disabled={loading}
                    >
                      <MenuItem value="APPROVED">Active</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                      <MenuItem value="REJECTED">Rejected</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.SECONDARY, mt: 0.5 }}>
                    Current status: <Chip 
                      label={frontendStatus} 
                      size="small" 
                      sx={{ 
                        ...statusStyles[formData.status], 
                        height: 18,
                        ml: 1 
                      }} 
                    />
                  </Typography>
                </Box> */}
              </Box>

              {/* Location Information Section */}
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 2 }}>
                Location Information
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                {/* City */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                    City
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.city}
                    onChange={handleInputChange('city')}
                    placeholder="Enter city"
                    sx={inputSx}
                    disabled={loading}
                  />
                </Box>

                {/* State */}
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                    State
                  </Typography>
                  <TextField
                    fullWidth
                    value={formData.state}
                    onChange={handleInputChange('state')}
                    placeholder="Enter state"
                    sx={inputSx}
                    disabled={loading}
                  />
                </Box>
              </Box>

              {/* Detailed Address */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>
                  Detailed Address
                </Typography>
                <TextField
                  fullWidth
                  value={formData.detailedAddress}
                  onChange={handleInputChange('detailedAddress')}
                  placeholder="Enter complete address"
                  multiline
                  rows={3}
                  sx={{
                    ...inputSx,
                    "& .MuiOutlinedInput-root": {
                      height: 'auto',
                    },
                  }}
                  disabled={loading}
                />
              </Box>

              <Divider sx={{ mb: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

              {/* Action Buttons */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    borderColor: COLORS.SECONDARY,
                    color: COLORS.SECONDARY,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name="check" />}
                  onClick={handleUpdate}
                  disabled={loading}
                  sx={{
                    bgcolor: COLORS.PRIMARY,
                    "&:hover": { bgcolor: COLORS.PRIMARY },
                    textTransform: "none",
                    minWidth: 120,
                  }}
                >
                  {loading ? "Updating..." : "Update"}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Box>

         <style>
        {`
          /* Disable scrollbar for modal content */
          .engineer-edit-modal ::-webkit-scrollbar {
            width: 0px !important;
            height: 0px !important;
            display: none !important;
          }
          
          .engineer-edit-modal ::-webkit-scrollbar-track {
            background: transparent !important;
          }
          
          .engineer-edit-modal ::-webkit-scrollbar-thumb {
            background: transparent !important;
            border-radius: 0px !important;
          }
          
          /* For Firefox */
          .engineer-edit-modal {
            scrollbar-width: none !important;
          }
          
          /* For IE and Edge */
          .engineer-edit-modal {
            -ms-overflow-style: none !important;
          }
          
          /* Specific styles for TextField scrollbars */
          .MuiOutlinedInput-root textarea::-webkit-scrollbar {
            width: 0px !important;
            height: 0px !important;
            display: none !important;
          }
          
          /* Ensure modal container doesn't show scrollbar */
          .MuiBox-root {
            scrollbar-width: none !important;
          }
          .MuiBox-root::-webkit-scrollbar {
            display: none !important;
          }
        `}
      </style>
    </>
  );
};

export default EngineerEditModal;